import { useState, useEffect } from "react";

import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

import { FormInput, FormSwitch, FormNumericTextBox } from "../FormComponents";
import { getAccountRef, getTargetClient } from "../../../data/clientData";

import {
  ExpansionPanel,
  ExpansionPanelContent,
} from "@progress/kendo-react-layout";
import { Reveal } from "@progress/kendo-react-animation";

import SkeletonForm from "../../Skeletons/SkeletonForm";

const EditClientForm = (props: any) => {
  const [accountRef, setAccountRef] = useState('')
  const [isDeleted, setIsDeleted] = useState(false);
  const [expandRequired, setExpandRequired] = useState(true);
  const [expandAccount, setExpandAccount] = useState(false);
  const [expandOptional, setExpandOptional] = useState(false);
  const [visible, setVisible] = useState(false);
  const [clientName, setClientName] = useState('')

  const [formState, setFormState] = useState({
    clientName: "",
    accountRef: "",
    contactFirstName: "",
    contactLastName: "",
    phone: "",
    email: "",
    clientAddr: "",
    postcode: "",
    phone2: "",
    fax: "",
    tradeContact: "",
    email2: "",
    email3: "",
    websiteUrl: "",
    vatNum: "",
    directdebitEmail: "",
    deletedDate: "",
    badDebter: false,
    defaultAdjustment: 0,
  });
  const [loading, setLoading] = useState(true);
  const clientId = props.onClientId;

  const toggleDeleteDialog = () => {
    setVisible(!visible);
  };

  useEffect(() => {
    const fetchPlaceholderData = async () => {
      const client = await getTargetClient(clientId);
      setFormState({
        clientName: client.clientName,
        accountRef: client.accountRef,
        contactFirstName: client.contactFirstName,
        contactLastName: client.contactLastName,
        phone: client.phone,
        email: client.email,
        clientAddr: client.clientAddr,
        postcode: client.postcode,
        phone2: client.phone2,
        fax: client.fax,
        tradeContact: client.tradeContact,
        email2: client.email2,
        email3: client.email3,
        websiteUrl: client.websiteUrl,
        vatNum: client.vatNum,
        directdebitEmail: client.directdebitEmail,
        deletedDate: client.deletedDate,
        badDebter: client.badDebter || false,
        defaultAdjustment: client.defaultAdjustment ?? 0,
      });

      setClientName(client.clientName)

      setLoading(false);
      if (client.deletedDate !== null) {
        setIsDeleted(true);
      } else {
        setIsDeleted(false);
      }
    };

    fetchPlaceholderData();
  }, [clientId]);

  const fetchAccountRef = async (client: string) => {
    await getAccountRef(client).then((result) => {
      setAccountRef(result)
    })
  }

  const handleRefGenerate = async () => {
    if(clientName !== '') {
      fetchAccountRef(clientName)
    } else {
      alert('Please enter a client name before generating a reference.')
    }
  }

  const handleSubmit = () => {
    // alert(JSON.stringify(formState, null, 2));
    if(formState.accountRef === '') {
      formState.accountRef = accountRef
    }
    // console.log(formState);
    props.onEditSubmit(formState, clientId);
  };

  const handleDelete = () => {
    props.onClientDelete(clientId);
    toggleDeleteDialog();
  };

  return loading ? (
    <SkeletonForm />
  ) : (
    <div className="form__wrapper">
      <div style={{ display: "flex", width: "100%" }}>
        <Form
          initialValues={{
            clientName: formState.clientName,
            firstName: formState.contactFirstName,
            lastName: formState.contactLastName, 
            email: formState.email,
            phone: formState.phone,
            clientAddr: formState.clientAddr,
            postcode: formState.postcode,
            accountRef: formState.accountRef,
          }}
          onSubmit={handleSubmit}
          render={(formRenderProps) => (
            <FormElement
              style={{ padding: "1rem", width: "100%", marginBottom: "5rem" }}
            >
              <fieldset className={"k-form-fieldset"}>
                <div className="form__subtitle">Edit client details:</div>

                <ExpansionPanel
                  title="Required Fields"
                  expandIcon="fa fa-plus"
                  collapseIcon="fa fa-minus"
                  expanded={expandRequired}
                  onAction={(e) => setExpandRequired(!e.expanded)}
                  style={{ marginBottom: "1rem" }}
                >
                  <Reveal>
                    {expandRequired && (
                      <ExpansionPanelContent>
                        <Field
                          name={"clientName"}
                          component={FormInput}
                          label={"Client Name:"}
                          placeholder={formState.clientName}
                          defaultValue={formState.clientName}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              clientName: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"contactFirstName"}
                          component={FormInput}
                          label={"Contact First Name"}
                          defaultValue={formState.contactFirstName}
                          placeholder={formState.contactFirstName}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              contactFirstName: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"contactLastName"}
                          component={FormInput}
                          label={"Contact Last Name"}
                          defaultValue={formState.contactLastName}
                          placeholder={formState.contactLastName}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              contactLastName: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"email"}
                          component={FormInput}
                          label={"Email Address:"}
                          defaultValue={formState.email}
                          placeholder={formState.email}
                          onChange={(e) => {
                            setFormState({ ...formState, email: e.value });
                          }}
                        />

                        <Field
                          name={"phone"}
                          component={FormInput}
                          label={"Phone Number:"}
                          defaultValue={formState.phone}
                          placeholder={formState.phone}
                          onChange={(e) => {
                            setFormState({ ...formState, phone: e.value });
                          }}
                        />

                        <Field
                          name={"clientAddr"}
                          component={FormInput}
                          label={"Address:"}
                          defaultValue={formState.clientAddr}
                          placeholder={formState.clientAddr}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              clientAddr: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"postcode"}
                          component={FormInput}
                          label={"Postcode:"}
                          defaultValue={formState.postcode}
                          placeholder={formState.postcode}
                          onChange={(e) => {
                            setFormState({ ...formState, postcode: e.value });
                          }}
                        />

                        <Field
                          name={"accountRef"}
                          component={FormInput}
                          label={"Account Reference:"}
                          placeholder={accountRef}
                          defaultText={accountRef}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              accountRef: e.value,
                            });
                          }}
                          hasButton={true}
                          buttonText={'Generate Reference'}
                          buttonAction={handleRefGenerate}
                        />
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>

                <ExpansionPanel
                  title="Account"
                  expandIcon="fa fa-plus"
                  collapseIcon="fa fa-minus"
                  expanded={expandAccount}
                  onAction={(e) => setExpandAccount(!e.expanded)}
                  style={{ marginBottom: "1rem" }}
                >
                  <Reveal>
                    {expandAccount && (
                      <ExpansionPanelContent>
                        <Field
                          name={"badDebter"}
                          component={FormSwitch}
                          label={"Bad Debter:"}
                          checked={formState.badDebter}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              badDebter: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"defaultAdjustment"}
                          component={FormNumericTextBox}
                          label={"Default Adjustment (%):"}
                          hint={
                            "Applied to the Adjustment (%) on the workings tab of new jobs for this client. Leave at zero for none."
                          }
                          format={"n2"}
                          step={0.5}
                          value={formState.defaultAdjustment}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              defaultAdjustment: e.value ?? 0,
                            });
                          }}
                        />
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>

                <ExpansionPanel
                  title="Optional Fields"
                  expandIcon="fa fa-plus"
                  collapseIcon="fa fa-minus"
                  expanded={expandOptional}
                  onAction={(e) => setExpandOptional(!e.expanded)}
                >
                  <Reveal>
                    {expandOptional && (
                      <ExpansionPanelContent>
                        <Field
                          name={"phone2"}
                          component={FormInput}
                          label={"Secondary Phone:"}
                          placeholder={formState.phone2}
                          onChange={(e) => {
                            setFormState({ ...formState, phone2: e.value });
                          }}
                        />

                        <Field
                          name={"fax"}
                          component={FormInput}
                          label={"Fax Address:"}
                          placeholder={formState.fax}
                          onChange={(e) => {
                            setFormState({ ...formState, fax: e.value });
                          }}
                        />

                        <Field
                          name={"tradeContact"}
                          component={FormInput}
                          label={"Trade Contact:"}
                          placeholder={formState.tradeContact}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              tradeContact: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"email2"}
                          component={FormInput}
                          label={"Second Email Address:"}
                          placeholder={formState.email2}
                          onChange={(e) => {
                            setFormState({ ...formState, email2: e.value });
                          }}
                        />

                        <Field
                          name={"email3"}
                          component={FormInput}
                          label={"Third Email Address:"}
                          placeholder={formState.email3}
                          onChange={(e) => {
                            setFormState({ ...formState, email3: e.value });
                          }}
                        />

                        <Field
                          name={"websiteUrl"}
                          component={FormInput}
                          label={"Website Address:"}
                          placeholder={formState.websiteUrl}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              websiteUrl: e.value,
                            });
                          }}
                        />

                        <Field
                          name={"vatNum"}
                          component={FormInput}
                          label={"VAT Number:"}
                          placeholder={formState.vatNum}
                          onChange={(e) => {
                            setFormState({ ...formState, vatNum: e.value });
                          }}
                        />

                        <Field
                          name={"directdebitEmail"}
                          component={FormInput}
                          label={"Direct Debit Email Address:"}
                          placeholder={formState.directdebitEmail}
                          onChange={(e) => {
                            setFormState({
                              ...formState,
                              directdebitEmail: e.value,
                            });
                          }}
                        />
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>

                {isDeleted ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      padding: "1rem 2rem 1rem 2rem",
                      position: "absolute",
                      zIndex: "100000",
                    }}
                  >
                    <button
                      onClick={() => {
                        formRenderProps.onFormReset();
                      }}
                      className="form__edit__button edit"
                    >
                      Reinstate Client
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      padding: "1rem 2rem 1rem 2rem",
                      position: "absolute",
                      zIndex: "100000",
                    }}
                  >
                    <button
                      onClick={() => {
                        handleSubmit();
                        formRenderProps.onFormReset();
                      }}
                      className="form__edit__button edit"
                    >
                      Save changes to Client
                    </button>
                    <button
                      onClick={toggleDeleteDialog}
                      className="form__edit__button reject"
                    >
                      Mark Client as Deleted
                    </button>
                  </div>
                )}
              </fieldset>
            </FormElement>
          )}
        />
      </div>
      {visible && (
        <Dialog title={"Please confirm"} onClose={toggleDeleteDialog}>
          <p
            style={{
              margin: "25px",
              textAlign: "center",
            }}
          >
            Are you sure you want to delete {formState.clientName}?
          </p>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={toggleDeleteDialog}
            >
              No
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={handleDelete}
            >
              Yes
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default EditClientForm;
